/**
 * Phase 36 Batch AE — Deal History Timeline.
 *
 * Renders the chronological event log for a deal on the Reports page
 * (and the Deal Detail → Report tab via <Reports dealId={...} compact />).
 *
 * Events come from GET /api/deals/:id/events and arrive newest-first.
 * Each row gets:
 *   * an icon keyed off event_type
 *   * the pre-rendered human_summary
 *   * a relative timestamp ("2h ago") with the absolute time on hover
 *   * an expandable "Details" toggle for AI runs / docs / stage notes
 *
 * Filters (chip row at the top): All | Price | Stage | AI | Documents.
 * Each filter maps to a comma-separated `type` query param.
 *
 * Accessibility: timeline uses semantic <ol> with <time dateTime=…>.
 *
 * IMPORTANT: uses the shared `api` instance — the 401 retry +
 * same-origin baseURL logic is critical here.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import {
  Activity,
  DollarSign,
  FileText,
  Loader2,
  Sparkles,
  TrendingUp,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Home,
  HandCoins,
} from "lucide-react";


// ──────────────────────────────────────────────────────────────────────
// Filter chip definitions. Order matters — first chip is the default.
// ──────────────────────────────────────────────────────────────────────
const FILTERS = [
  { id: "all", label: "All events", types: null },
  {
    id: "price",
    label: "Price changes",
    types: [
      "purchase_price_changed",
      "asking_price_changed",
      "counter_offer_made",
      "target_offer_set",
      "rent_changed",
      "rehab_budget_changed",
    ],
  },
  { id: "stage", label: "Stage moves", types: ["stage_changed"] },
  { id: "ai", label: "AI analysis", types: ["ai_negotiation_run", "ai_verdict_run"] },
  { id: "docs", label: "Documents", types: ["document_uploaded", "document_analyzed"] },
];


// Icon + tone for each event type. Keep tones muted (slate/indigo/emerald/
// amber/rose) so the timeline stays calm — the whole point is scanning.
function eventVisuals(eventType) {
  switch (eventType) {
    case "deal_created":
      return { Icon: Home, ring: "ring-indigo-100", tone: "text-indigo-600 bg-indigo-50" };
    case "purchase_price_changed":
    case "asking_price_changed":
    case "rent_changed":
    case "rehab_budget_changed":
      return { Icon: DollarSign, ring: "ring-emerald-100", tone: "text-emerald-700 bg-emerald-50" };
    case "counter_offer_made":
    case "target_offer_set":
      return { Icon: HandCoins, ring: "ring-amber-100", tone: "text-amber-700 bg-amber-50" };
    case "stage_changed":
      return { Icon: Activity, ring: "ring-slate-100", tone: "text-slate-700 bg-slate-100" };
    case "ai_negotiation_run":
    case "ai_verdict_run":
      return { Icon: Sparkles, ring: "ring-indigo-100", tone: "text-indigo-700 bg-indigo-50" };
    case "document_uploaded":
    case "document_analyzed":
      return { Icon: FileText, ring: "ring-violet-100", tone: "text-violet-700 bg-violet-50" };
    default:
      return { Icon: TrendingUp, ring: "ring-slate-100", tone: "text-slate-600 bg-slate-50" };
  }
}


function relativeTime(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "";
  const diff = Date.now() - t;
  const s = Math.round(diff / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  // Older than a week → show the date itself
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}


function _fmt$(v) {
  if (typeof v !== "number" || !Number.isFinite(v)) return null;
  return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}


function EventRow({ event }) {
  const [expanded, setExpanded] = useState(false);
  const { Icon, ring, tone } = eventVisuals(event.event_type);
  const absolute = new Date(event.created_at).toLocaleString();
  // Some events have meaningful expandable detail; everything else
  // hides the toggle.
  const canExpand = useMemo(() => {
    if (event.event_type === "ai_negotiation_run") return true;
    if (event.event_type === "document_analyzed") return true;
    if (event.event_type === "stage_changed" && event.context?.note) return true;
    return false;
  }, [event]);

  return (
    <li
      data-testid={`history-event-${event.event_type}`}
      className="relative pl-12"
    >
      {/* Vertical rail dot */}
      <span
        className={`absolute left-3 top-1.5 flex h-7 w-7 -translate-x-1/2 items-center justify-center rounded-full ring-4 ${ring} ${tone}`}
      >
        <Icon className="h-3.5 w-3.5" />
      </span>
      <div className="rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          <p className="min-w-0 flex-1 text-sm font-medium text-slate-900">
            {event.human_summary}
          </p>
          <time
            dateTime={event.created_at}
            title={absolute}
            className="flex-none text-xs text-slate-500 tabular-nums"
          >
            {relativeTime(event.created_at)}
          </time>
        </div>
        {canExpand && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            data-testid={`history-event-toggle-${event.id}`}
            className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-indigo-700 hover:underline"
          >
            {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {expanded ? "Hide details" : "Show details"}
          </button>
        )}
        {expanded && (
          <EventDetailBlock event={event} />
        )}
      </div>
    </li>
  );
}


function EventDetailBlock({ event }) {
  // Renderers per event_type so the expanded UI stays tight.
  if (event.event_type === "ai_negotiation_run") {
    const n = event.new_value || {};
    const ctx = event.context || {};
    const signals = ctx.document_signals_used || [];
    return (
      <div
        data-testid={`history-detail-ai-${event.id}`}
        className="mt-3 space-y-2 rounded-lg bg-slate-50 p-3 text-xs"
      >
        <div className="grid grid-cols-3 gap-2 text-center">
          {[
            ["Conservative", n.conservative_offer],
            ["Recommended", n.recommended_offer],
            ["Max", n.max_offer],
          ].map(([k, v]) => (
            <div key={k} className="rounded-md border border-slate-200 bg-white px-2 py-1.5">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                {k}
              </div>
              <div className="mt-0.5 text-sm font-bold tabular-nums text-slate-900">
                {_fmt$(v) || "—"}
              </div>
            </div>
          ))}
        </div>
        {Number.isFinite(n.document_adjustment_pct) && n.doc_signals_count > 0 && (
          <p className="text-xs text-slate-600">
            Document adjustment:{" "}
            <span
              className={
                n.document_adjustment_pct < 0
                  ? "font-bold text-rose-700"
                  : n.document_adjustment_pct > 0
                  ? "font-bold text-emerald-700"
                  : "font-bold text-slate-700"
              }
            >
              {n.document_adjustment_pct > 0 ? "+" : ""}
              {n.document_adjustment_pct.toFixed(1)}%
            </span>{" "}
            from {n.doc_signals_count} document{n.doc_signals_count === 1 ? "" : "s"}
          </p>
        )}
        {signals.length > 0 && (
          <ul className="space-y-1">
            {signals.slice(0, 5).map((s, i) => (
              <li key={s.doc_id || i} className="text-xs text-slate-600">
                <span className="font-medium">{s.category_label || s.category}</span>
                {Number.isFinite(s.weighted_impact_pct) && (
                  <span className="ml-1 text-slate-500">
                    ({s.weighted_impact_pct > 0 ? "+" : ""}{s.weighted_impact_pct}%)
                  </span>
                )}
                {s.headline_red_flag && (
                  <span className="ml-1 text-slate-500"> — {s.headline_red_flag}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  if (event.event_type === "document_analyzed") {
    const n = event.new_value || {};
    const ctx = event.context || {};
    return (
      <div
        data-testid={`history-detail-doc-${event.id}`}
        className="mt-3 space-y-1 rounded-lg bg-slate-50 p-3 text-xs text-slate-700"
      >
        <div>
          <span className="font-semibold">{n.filename || n.doc_id}</span>{" "}
          <span className="text-slate-500">· {n.category}</span>
        </div>
        {Array.isArray(ctx.red_flags) && ctx.red_flags.length > 0 && (
          <ul className="ml-4 list-disc space-y-0.5 text-slate-600">
            {ctx.red_flags.map((rf, i) => (
              <li key={i} className="text-xs">{rf}</li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  if (event.event_type === "stage_changed" && event.context?.note) {
    return (
      <p
        data-testid={`history-detail-stage-${event.id}`}
        className="mt-3 rounded-lg bg-slate-50 p-3 text-xs italic text-slate-600"
      >
        Note: “{event.context.note}”
      </p>
    );
  }

  return null;
}


export default function DealHistoryTimeline({ dealId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all");
  const [visibleCount, setVisibleCount] = useState(20);

  // Posthog fired once per (deal × first-render) via a ref so the
  // effect doesn't have to setState during render — keeps the
  // react-hooks/set-state-in-effect rule happy.
  const trackedRef = useRef(false);

  const fetchEvents = useCallback(async () => {
    if (!dealId) return;
    setLoading(true);
    setErrorMsg(null);
    try {
      const f = FILTERS.find((x) => x.id === activeFilter);
      const params = { limit: 200 };
      if (f?.types) params.type = f.types.join(",");
      const { data } = await api.get(`/deals/${dealId}/events`, { params });
      setEvents(data.events || []);
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || e?.message || "Could not load history.");
    } finally {
      setLoading(false);
    }
  }, [dealId, activeFilter]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    if (trackedRef.current || loading || errorMsg) return;
    trackedRef.current = true;
    try {
      window.posthog?.capture?.("report_history_viewed", {
        deal_id: dealId,
        event_count: events.length,
      });
    } catch {
      /* DNT — ignore */
    }
  }, [loading, errorMsg, dealId, events.length]);

  const handleFilterChange = (id) => {
    if (id === activeFilter) return;
    setActiveFilter(id);
    setVisibleCount(20);
    try {
      window.posthog?.capture?.("report_history_filter_changed", {
        deal_id: dealId,
        filter: id,
      });
    } catch {
      /* ignore */
    }
  };

  const visible = events.slice(0, visibleCount);

  return (
    <section
      data-testid="deal-history-timeline"
      className="mt-8 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6"
    >
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-bold tracking-tight text-slate-900 sm:text-lg">
            Deal History
          </h3>
          <p className="mt-0.5 text-sm text-slate-500">
            Chronological log of price changes, AI analyses, stage moves, and document activity.
          </p>
        </div>
        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700 tabular-nums">
          {events.length} event{events.length === 1 ? "" : "s"}
        </span>
      </header>

      <div className="flex flex-wrap gap-2 border-b border-slate-100 pb-3">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => handleFilterChange(f.id)}
            data-testid={`history-filter-${f.id}`}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              activeFilter === f.id
                ? "bg-indigo-600 text-white"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div
          data-testid="history-loading"
          className="flex items-center justify-center gap-2 p-12 text-sm text-slate-500"
        >
          <Loader2 className="h-4 w-4 animate-spin" /> Loading history…
        </div>
      ) : errorMsg ? (
        <div
          data-testid="history-error"
          className="mt-4 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div className="min-w-0">
            <p className="font-medium">Could not load history</p>
            <p className="mt-0.5 text-xs">{errorMsg}</p>
          </div>
        </div>
      ) : events.length === 0 ? (
        <div
          data-testid="history-empty"
          className="mt-4 rounded-lg border border-dashed border-slate-200 p-8 text-center text-sm text-slate-500"
        >
          {activeFilter === "all"
            ? "No events recorded yet."
            : "No events match the selected filter."}
        </div>
      ) : (
        <>
          <ol
            data-testid="history-list"
            className="relative mt-5 space-y-3 before:absolute before:bottom-0 before:left-3 before:top-0 before:w-px before:bg-slate-200"
          >
            {visible.map((e) => (
              <EventRow key={e.id} event={e} />
            ))}
          </ol>
          {visibleCount < events.length && (
            <div className="mt-4 flex justify-center">
              <button
                type="button"
                onClick={() => setVisibleCount((v) => v + 20)}
                data-testid="history-load-more"
                className="rounded-md border border-slate-200 bg-white px-4 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
              >
                Load more ({events.length - visibleCount} remaining)
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
